import { useState, useEffect } from "react";
import DonutMeter from "./DonutMeter";
import { C } from "./ui/theme";
// Phase 3 (standalone page-file token migration, see linear-brewing-toast.md)
// — replaced the local `T` Fluent palette with the shared `C` Apple tokens.
// T's `greenL`/`redL`/`amberL`/`white` didn't exist under those names on C,
// so those four were mapped onto their C equivalents (`greenMuted`/
// `redMuted`/`amberMuted`/`bgCard` — same soft-fill/card-surface role,
// just the app-wide token names) rather than left as a blind string swap
// that would have silently broken (C has no `.greenL` etc., so those would
// have resolved to `undefined`).
const PRESETS = [
  { label:"Gmail", smtpHost:"smtp.gmail.com", smtpPort:"587", imapHost:"imap.gmail.com", imapPort:"993" },
  { label:"Outlook/M365", smtpHost:"smtp.office365.com", smtpPort:"587", imapHost:"outlook.office365.com", imapPort:"993" },
  { label:"GoDaddy M365", smtpHost:"smtp.office365.com", smtpPort:"587", imapHost:"outlook.office365.com", imapPort:"993" },
  { label:"Yahoo", smtpHost:"smtp.mail.yahoo.com", smtpPort:"587", imapHost:"imap.mail.yahoo.com", imapPort:"993" },
  { label:"Custom", smtpHost:"", smtpPort:"587", imapHost:"", imapPort:"993" },
];
export default function EmailWarmup({addToast=()=>{}}){
  const [accounts,setAccounts]=useState([]);
  const [stats,setStats]=useState({totalAccounts:0,active:0,emailsSent:0,avgHealth:0});
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({email:"",name:"",password:"",smtpHost:"smtp.office365.com",smtpPort:"587",imapHost:"outlook.office365.com",imapPort:"993",dailyLimit:"20",warmupLimit:"10"});
  const [loading,setLoading]=useState(false);
  const [running,setRunning]=useState(false);
  const [search,setSearch]=useState("");
  // Deliverability Black Box Recorder — the "what actually happened" log for
  // every send, independent of warmup accounts (covers all connected
  // mailboxes and Mail Merge/Sequence sends, not just warmup-enrolled ones).
  const [showDelivLog,setShowDelivLog]=useState(false);
  const [delivSummary,setDelivSummary]=useState({senders:[]});
  const [delivLogs,setDelivLogs]=useState([]);
  const [delivLoading,setDelivLoading]=useState(false);
  const [delivEventFilter,setDelivEventFilter]=useState("");
  const [delivSenderFilter,setDelivSenderFilter]=useState("");
  const tk=()=>localStorage.getItem("sdr_token")||"";
  const authH={"Authorization":"Bearer "+tk(),"Content-Type":"application/json"};
  const fetchStats=async()=>{
    try{const r=await fetch("/api/warmup/stats",{headers:authH});const d=await r.json();setStats(d);setAccounts(d.accounts||[]);}catch(e){}
  };
  useEffect(()=>{fetchStats();const t=setInterval(fetchStats,30000);return()=>clearInterval(t);},[]);
  const fetchDelivSummary=async()=>{
    try{const r=await fetch("/api/deliverability/summary",{headers:authH});const d=await r.json();setDelivSummary(d);}catch(e){}
  };
  const fetchDelivLogs=async()=>{
    setDelivLoading(true);
    try{
      const params=new URLSearchParams({limit:"100"});
      if(delivEventFilter)params.set("event",delivEventFilter);
      if(delivSenderFilter)params.set("senderEmail",delivSenderFilter);
      const r=await fetch("/api/deliverability/logs?"+params.toString(),{headers:authH});
      const d=await r.json();
      setDelivLogs(d.logs||[]);
    }catch(e){}
    setDelivLoading(false);
  };
  useEffect(()=>{fetchDelivSummary();},[]);
  useEffect(()=>{if(showDelivLog)fetchDelivLogs();},[showDelivLog,delivEventFilter,delivSenderFilter]);
  const delivEventColor=ev=>ev==="sent"?C.green:ev==="bounced"||ev==="failed"?C.red:C.amber;
  const applyPreset=(p)=>setForm(f=>({...f,smtpHost:p.smtpHost,smtpPort:p.smtpPort,imapHost:p.imapHost,imapPort:p.imapPort}));
  const addAccount=async()=>{
    if(!form.email||!form.password||!form.smtpHost)return addToast("Email, password and SMTP host required","error");
    setLoading(true);
    try{
      const r=await fetch("/api/warmup/accounts",{method:"POST",headers:authH,body:JSON.stringify(form)});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error||"Failed to add account");
      addToast("Warmup account added","success");
      setShowAdd(false);
      setForm({email:"",name:"",password:"",smtpHost:"smtp.office365.com",smtpPort:"587",imapHost:"outlook.office365.com",imapPort:"993",dailyLimit:"20",warmupLimit:"10"});
      fetchStats();
    }catch(e){addToast(e.message,"error");}
    setLoading(false);
  };
  const toggleAccount=async(id)=>{
    try{const r=await fetch("/api/warmup/accounts/"+id+"/toggle",{method:"POST",headers:authH});const d=await r.json();addToast(d.message||"Updated","success");fetchStats();}catch(e){addToast(e.message,"error");}
  };
  const deleteAccount=async(id)=>{
    try{await fetch("/api/warmup/accounts/"+id,{method:"DELETE",headers:authH});addToast("Account removed","info");fetchStats();}catch(e){addToast(e.message,"error");}
  };
  const runNow=async()=>{
    setRunning(true);
    try{const r=await fetch("/api/warmup/run",{method:"POST",headers:authH});const d=await r.json();addToast(d.message||"Warmup run complete","success");}catch(e){addToast(e.message,"error");}
    setRunning(false);
  };
  const filtered=accounts.filter(a=>(a.email||"").toLowerCase().includes(search.toLowerCase())||(a.name||"").toLowerCase().includes(search.toLowerCase()));
  const scoreColor=s=>s>=80?"#16a34a":s>=60?"#d97706":"#ef4444";
  const inp={width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"8px 12px",fontSize:13,color:C.text,outline:"none",background:C.bgCard,boxSizing:"border-box"};
  const lbl={fontSize:12,color:C.textSub,marginBottom:4,display:"block",fontWeight:500};
  return(
    <div style={{padding:"0 0 40px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
        <div>
          <h2 style={{fontSize:22,fontWeight:700,color:C.text,margin:0}}>Email Warmup</h2>
          <p style={{fontSize:13,color:C.textSub,marginTop:4}}>Build sender reputation automatically using SMTP and IMAP</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={runNow} disabled={running} style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:6,padding:"8px 16px",fontSize:13,cursor:"pointer",color:C.text}}>{running?"Running...":"Run Now"}</button>
          <button onClick={()=>setShowAdd(!showAdd)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:6,padding:"8px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>+ Add Account</button>
        </div>
      </div>
      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:24}}>
        {[["TOTAL ACCOUNTS",stats.totalAccounts||0,"#0078D4"],["ACTIVE",stats.active||0,"#107C10"],["EMAILS SENT",stats.emailsSent||0,"#0078D4"]].map(([l,v,c])=>(
          <div key={l} style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:"20px 24px"}}>
            <p style={{fontSize:11,fontWeight:600,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:8}}>{l}</p>
            <p style={{fontSize:28,fontWeight:700,color:c,margin:0}}>{v}</p>
          </div>
        ))}
        <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:"12px 24px",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <DonutMeter value={stats.avgHealth||0} size={72} strokeWidth={8} label="AVG HEALTH" />
        </div>
      </div>
      {showAdd&&(
        <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:24,marginBottom:24}}>
          <h3 style={{fontSize:16,fontWeight:600,margin:"0 0 16px"}}>Add Warmup Account</h3>
          <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
            {PRESETS.map(p=>(
              <button key={p.label} onClick={()=>applyPreset(p)} style={{background:form.smtpHost===p.smtpHost&&p.label!=="Custom"?C.accent:C.bgCard,color:form.smtpHost===p.smtpHost&&p.label!=="Custom"?"#fff":C.textSub,border:"1px solid "+C.border,borderRadius:6,padding:"4px 12px",fontSize:12,cursor:"pointer"}}>{p.label}</button>
            ))}
          </div>
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={lbl}>EMAIL ADDRESS *</label><input value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="anji@sdrreachout.com" style={inp}/></div>
            <div><label style={lbl}>DISPLAY NAME</label><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Anji Reddy" style={inp}/></div>
            <div style={{gridColumn:"1/-1"}}><label style={lbl}>PASSWORD (or App Password for Gmail/Yahoo)</label><input type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="Your email password" style={inp}/></div>
            <div><label style={lbl}>SMTP HOST</label><input value={form.smtpHost} onChange={e=>setForm(f=>({...f,smtpHost:e.target.value}))} placeholder="smtp.office365.com" style={inp}/></div>
            <div><label style={lbl}>SMTP PORT</label><input value={form.smtpPort} onChange={e=>setForm(f=>({...f,smtpPort:e.target.value}))} placeholder="587" style={inp}/></div>
            <div><label style={lbl}>IMAP HOST</label><input value={form.imapHost} onChange={e=>setForm(f=>({...f,imapHost:e.target.value}))} placeholder="outlook.office365.com" style={inp}/></div>
            <div><label style={lbl}>IMAP PORT</label><input value={form.imapPort} onChange={e=>setForm(f=>({...f,imapPort:e.target.value}))} placeholder="993" style={inp}/></div>
            <div><label style={lbl}>DAILY SEND LIMIT</label><input type="number" value={form.dailyLimit} onChange={e=>setForm(f=>({...f,dailyLimit:e.target.value}))} placeholder="20" style={inp}/></div>
            <div><label style={lbl}>WARMUP EMAILS/DAY</label><input type="number" value={form.warmupLimit} onChange={e=>setForm(f=>({...f,warmupLimit:e.target.value}))} placeholder="10" style={inp}/></div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={addAccount} disabled={loading} style={{background:C.accent,color:"#fff",border:"none",borderRadius:6,padding:"9px 20px",fontSize:13,fontWeight:600,cursor:"pointer",opacity:loading?.7:1}}>{loading?"Adding...":"Add Account"}</button>
            <button onClick={()=>setShowAdd(false)} style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:6,padding:"9px 16px",fontSize:13,cursor:"pointer",color:C.textSub}}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search accounts..." style={{...inp,width:280,maxWidth:"100%",boxSizing:"border-box"}}/>
          <span style={{fontSize:13,color:C.textSub}}>{filtered.length} accounts</span>
        </div>
        <div className="hide-on-mobile" style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 120px",gap:8,padding:"8px 12px",background:C.bg,borderRadius:6,marginBottom:8,fontSize:11,fontWeight:600,color:C.textSub,textTransform:"uppercase",letterSpacing:".4px"}}>
          <span>EMAIL</span><span>EMAILS SENT</span><span>WARMUP SENT</span><span>HEALTH SCORE</span><span>STATUS</span><span>ACTIONS</span>
        </div>
        {filtered.length===0&&<p style={{textAlign:"center",color:C.textSub,fontSize:13,padding:"32px 0"}}>No accounts yet. Click "+ Add Account" to get started.</p>}
        {filtered.map(a=>(
          <div key={a._id||a.id} className="responsive-grid" style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 120px",gap:8,padding:"12px",borderBottom:"1px solid "+C.border,alignItems:"center"}}>
            <div><p style={{fontWeight:600,fontSize:13,margin:0}}>{a.name||a.email}</p><p style={{fontSize:11,color:C.textSub,margin:0}}>{a.email}</p></div>
            <span style={{fontSize:13}}>{a.emailsSent||0}</span>
            <span style={{fontSize:13}}>{a.warmupSent||0}</span>
            <span style={{fontSize:13,fontWeight:600,color:scoreColor(a.healthScore||0)}}>{a.healthScore||0}%</span>
            <span style={{fontSize:11,padding:"3px 8px",borderRadius:99,background:a.active?C.greenMuted:C.amberMuted,color:a.active?C.green:C.amber,fontWeight:600}}>{a.active?"Active":"Paused"}</span>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>toggleAccount(a._id||a.id)} style={{fontSize:11,padding:"4px 8px",borderRadius:4,border:"1px solid "+C.border,background:C.bgCard,cursor:"pointer",color:C.accent}}>{a.active?"Pause":"Start"}</button>
              <button onClick={()=>deleteAccount(a._id||a.id)} style={{fontSize:11,padding:"4px 8px",borderRadius:4,border:"1px solid "+C.border,background:C.bgCard,cursor:"pointer",color:C.red}}>Remove</button>
            </div>
          </div>
        ))}
      </div>

      {/* Deliverability Black Box Recorder — per-send diagnostic log, so
          "why did deliverability drop" has an actual record to check
          instead of a guess. Covers every send path (Graph/Gmail/Resend),
          not just warmup-enrolled accounts. */}
      <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:24,marginTop:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:showDelivLog?16:0}}>
          <div>
            <h3 style={{fontSize:16,fontWeight:600,margin:0,color:C.text}}>Deliverability Log</h3>
            <p style={{fontSize:12,color:C.textSub,marginTop:4}}>Per-send record of what actually happened — sent, bounced, or failed, with the provider's own reason.</p>
          </div>
          <button onClick={()=>setShowDelivLog(v=>!v)} style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:6,padding:"7px 14px",fontSize:12,cursor:"pointer",color:C.text}}>{showDelivLog?"Hide":"View log"}</button>
        </div>

        {delivSummary.senders.length>0&&(
          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:showDelivLog?0:16}}>
            {delivSummary.senders.map(s=>{
              const total=(s.sent||0)+(s.bounced||0)+(s.deferred||0)+(s.complained||0)+(s.failed||0);
              const bad=(s.bounced||0)+(s.complained||0)+(s.failed||0);
              const badRate=total>0?Math.round((bad/total)*100):0;
              return(
                <div key={s.senderEmail} style={{border:"1px solid "+C.border,borderRadius:8,padding:"8px 12px",fontSize:11,minWidth:160}}>
                  <p style={{fontWeight:600,color:C.text,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.senderEmail}</p>
                  <p style={{color:C.textSub,margin:"2px 0 0"}}>Last 7 days: {total} sends</p>
                  <p style={{color:badRate>10?C.red:C.textSub,margin:"2px 0 0",fontWeight:badRate>10?600:400}}>{bad} bounced/failed/complained{badRate>10?` (${badRate}%)`:""}</p>
                </div>
              );
            })}
          </div>
        )}

        {showDelivLog&&(
          <div style={{marginTop:16}}>
            <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
              <select value={delivEventFilter} onChange={e=>setDelivEventFilter(e.target.value)} style={{...inp,width:160}}>
                <option value="">All events</option>
                <option value="sent">Sent</option>
                <option value="bounced">Bounced</option>
                <option value="deferred">Deferred</option>
                <option value="complained">Complained</option>
                <option value="failed">Failed</option>
              </select>
              <input value={delivSenderFilter} onChange={e=>setDelivSenderFilter(e.target.value)} placeholder="Filter by sender email..." style={{...inp,width:240}}/>
              <button onClick={fetchDelivLogs} style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:6,padding:"7px 14px",fontSize:12,cursor:"pointer",color:C.textSub}}>Refresh</button>
            </div>
            <div className="hide-on-mobile" style={{display:"grid",gridTemplateColumns:"110px 1fr 1fr 90px 90px 1fr",gap:8,padding:"8px 12px",background:C.bg,borderRadius:6,marginBottom:6,fontSize:11,fontWeight:600,color:C.textSub,textTransform:"uppercase",letterSpacing:".4px"}}>
              <span>WHEN</span><span>SENDER</span><span>RECIPIENT</span><span>EVENT</span><span>VIA</span><span>REASON</span>
            </div>
            {delivLoading?(
              <p style={{textAlign:"center",color:C.textSub,fontSize:13,padding:"24px 0"}}>Loading…</p>
            ):delivLogs.length===0?(
              <p style={{textAlign:"center",color:C.textSub,fontSize:13,padding:"24px 0"}}>No send events recorded yet.</p>
            ):delivLogs.map(l=>(
              <div key={l.id} className="responsive-grid" style={{display:"grid",gridTemplateColumns:"110px 1fr 1fr 90px 90px 1fr",gap:8,padding:"8px 12px",borderBottom:"1px solid "+C.border,alignItems:"center",fontSize:12}}>
                <span style={{color:C.textSub}}>{new Date(l.createdAt).toLocaleString(undefined,{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.senderEmail}</span>
                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.recipientEmail}</span>
                <span style={{fontWeight:600,color:delivEventColor(l.event)}}>{l.event}</span>
                <span style={{color:C.textSub}}>{l.via}</span>
                <span style={{color:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={l.reasonText||""}>{l.reasonCode||l.reasonText||"—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}